import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  CartChangesModalComponent,
  CartChangesModalData,
  CartRemovedLine,
  CartRepricedLine,
} from '@components/UI/cart-changes-modal/cart-changes-modal.component';

import { ModalService } from '@services/components/ModalService/modal.service';
import {
  CreateOrderItemRequest,
  ValidateCartResponse,
} from '@services/orders/interfaces/order.interfaces';
import { OrdersService } from '@services/orders/orders.service';
import { catalogErrorFrom, reasonTextFor } from '@shared/constants/cart-availability.constants';

import { CartItem, CartItemCatalogUpdate } from './interfaces/cart.interfaces';
import { CartService } from './cart-services';

const CATALOG_ROUTE = '/catalogo';

/**
 * El carrito se revalida al abrirlo y otra vez al entrar al checkout. Sin una
 * ventana mínima entre llamadas, abrir y cerrar el carrito varias veces seguidas
 * gastaría el rate limit del endpoint (20 por minuto) sin aportar nada: el
 * catálogo no cambia en esos segundos.
 */
const REVALIDATION_THROTTLE_MS = 30_000;

export interface CartValidationOutcome {
  removed: CartRemovedLine[];
  repriced: CartRepricedLine[];
  cartEmptied: boolean;
}

@Injectable({ providedIn: 'root' })
export class CartValidationService {
  private readonly cartService = inject(CartService);
  private readonly ordersService = inject(OrdersService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  /**
   * Nombres de las líneas que solo se pueden retirar en tienda. Se deriva del
   * carrito vigente, así que sacar la línea vuelve a habilitar el despacho.
   */
  public readonly itemNamesWithoutDelivery = computed(() => {
    const cartItemIdsWithoutDelivery = this.cartItemIdsWithoutDelivery();

    return [...new Set(
      this.cartService.cartItems()
        .filter((item) => cartItemIdsWithoutDelivery.has(item.id))
        .map((item) => item.name),
    )];
  });

  public readonly isDeliveryAvailable = computed(() => this.itemNamesWithoutDelivery().length === 0);

  private readonly cartItemIdsWithoutDelivery = signal<ReadonlySet<string>>(new Set());

  private lastValidationAt = 0;

  public revalidate(force = false): Observable<CartValidationOutcome | null> {
    const items = this.cartService.cartItems();

    if (!items.length) {
      return of(null);
    }

    if (!force && Date.now() - this.lastValidationAt < REVALIDATION_THROTTLE_MS) {
      return of(null);
    }

    return this.ordersService
      .validateCart({ items: items.map((item) => this.toOrderItem(item)) })
      .pipe(
        tap((validation) => {
          this.lastValidationAt = Date.now();
          this.trackItemsWithoutDelivery(items, validation);
        }),
        map((validation) => this.applyValidation(items, validation)),
        // Si la validación falla no se toca el carrito: vaciarlo por un error de
        // red sería peor que dejar pasar un precio viejo, que el back corrige.
        catchError(() => of(null)),
      );
  }

  public announce(outcome: CartValidationOutcome): Observable<boolean> {
    const data: CartChangesModalData = {
      removed: outcome.removed,
      repriced: outcome.repriced,
      isCartEmpty: outcome.cartEmptied,
    };

    return this.modalService
      .openModal<boolean, CartChangesModalData>({
        title: outcome.removed.length
          ? 'Actualizamos tu carrito'
          : 'Actualizamos los precios',
        component: CartChangesModalComponent,
        data,
        closeOnBackdrop: !outcome.cartEmptied,
        closeOnEsc: !outcome.cartEmptied,
        showCloseButton: !outcome.cartEmptied,
      })
      .pipe(
        map((event) => {
          const goToCatalog = outcome.cartEmptied || event.reason === 'confirm';

          if (goToCatalog) {
            this.cartService.closeCart();
            this.router.navigateByUrl(CATALOG_ROUTE);
          }

          return goToCatalog;
        }),
      );
  }

  public revalidateAndAnnounce(force = false): Observable<CartValidationOutcome | null> {
    return this.revalidate(force).pipe(
      tap((outcome) => {
        if (outcome) {
          this.announce(outcome).subscribe();
        }
      }),
    );
  }

  public handleCatalogError(error: HttpErrorResponse): boolean {
    const catalogError = catalogErrorFrom(error);

    if (!catalogError) {
      return false;
    }

    const removedItems = this.cartService.removeByProductId(catalogError.productId);
    const reason = reasonTextFor(catalogError.reasonCode);

    // El producto podría no estar ya en el carrito (otra pestaña lo sacó): se
    // avisa igual, porque el cliente venía de intentar pagarlo.
    const removed: CartRemovedLine[] = removedItems.length
      ? removedItems.map((item) => ({ name: item.name, reason }))
      : [{ name: 'Uno de los productos de tu pedido', reason }];

    this.announce({
      removed,
      repriced: [],
      cartEmptied: this.cartService.cartItems().length === 0,
    }).subscribe((navigatedToCatalog) => {
      if (!navigatedToCatalog) {
        this.cartService.openCart();
      }
    });

    return true;
  }

  public toOrderItem(item: CartItem): CreateOrderItemRequest {
    if (item.productType === 'keychain') {
      const isCombination = item.variant.keychainVariant === 'combination';

      return {
        productId: item.productId,
        productType: 'keychain',
        quantity: item.quantity,
        keychainType: isCombination ? 'combination' : 'color',
        thicknessId: item.variant.thicknessId ?? undefined,
        customName: item.variant.keychainName,
        frontColorId: isCombination ? item.variant.keychainFrontColorId ?? undefined : undefined,
        backColorId: isCombination ? item.variant.keychainBackColorId ?? undefined : undefined,
        colorId: isCombination ? undefined : item.variant.keychainColorId ?? undefined,
      };
    }

    return {
      productId: item.productId,
      productType: 'standard',
      quantity: item.quantity,
      sizeId: item.variant.sizeId ?? undefined,
      colorId: item.variant.colorId ?? undefined,
    };
  }

  /**
   * The shipping quote can also detect pickup-only lines (e.g. the admin changed the
   * dimensions after the last revalidation). Lines are matched by cart item id, never by
   * product name, so renamed or homonymous products are not confused.
   */
  public markItemsWithoutDelivery(cartItemIds: string[]): void {
    this.cartItemIdsWithoutDelivery.update((currentIds) => new Set([...currentIds, ...cartItemIds]));
  }

  private applyValidation(
    items: CartItem[],
    validation: ValidateCartResponse,
  ): CartValidationOutcome | null {
    if (validation.items.length !== items.length) {
      return null;
    }

    const removed: CartRemovedLine[] = [];
    const repriced: CartRepricedLine[] = [];

    const updates: CartItemCatalogUpdate[] = items.map((item, index) => {
      const result = validation.items[index];

      if (!result || !result.available) {
        removed.push({
          name: item.name,
          reason: reasonTextFor(result?.reasonCode),
        });

        return { id: item.id, available: false };
      }

      if (result.unitPrice !== null && result.unitPrice !== item.unitPrice) {
        repriced.push({
          name: result.productName ?? item.name,
          previousPrice: item.unitPrice,
          unitPrice: result.unitPrice,
        });
      }

      return {
        id: item.id,
        available: true,
        name: result.productName,
        unitPrice: result.unitPrice,
      };
    });

    if (!removed.length && !repriced.length) {
      return null;
    }

    this.cartService.syncWithCatalog(updates);

    return {
      removed,
      repriced,
      cartEmptied: this.cartService.cartItems().length === 0,
    };
  }

  private trackItemsWithoutDelivery(items: CartItem[], validation: ValidateCartResponse): void {
    if (validation.items.length !== items.length) {
      return;
    }

    const cartItemIds = items
      .filter((_item, index) => validation.items[index]?.available && validation.items[index]?.hasHomeDelivery === false)
      .map((item) => item.id);

    this.cartItemIdsWithoutDelivery.set(new Set(cartItemIds));
  }
}
